import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getRecurringHistory,
  markDone,
  recurringHistoryKey,
  type RecurringItem,
} from '@/api/recurring'
import { fetchSpendMonthSuggestion } from '@/api/transactions'
import { DatePicker } from '@/components/DatePicker'
import { SpendMonthField } from '@/components/transactions/add/SpendMonthField'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Sheet } from '@/components/ui/sheet'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { formatShortDate } from '@/lib/formatDate'
import { addMonths, monthOf } from '@/lib/spendMonth'
import { fromPaise, scaleSharesPaise, toPaise } from '@/lib/splitMath'
import { cn } from '@/lib/utils'

const LABEL_CLS = 'mb-1 block text-sm font-medium text-[var(--fl-text-1)]'

function parsePaise(text: string): number | null {
  const trimmed = text.trim().replace(/,/g, '')
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? toPaise(n) : null
}

function errorDetail(err: unknown): string | undefined {
  return (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
}

/** Mark as Paid / Received for a plain reminder: confirm or change the date and amount. */
export function MarkDoneSheet({
  item,
  onClose,
  onDone,
}: {
  item: RecurringItem
  onClose: () => void
  onDone: (message: string) => void
}) {
  const queryClient = useQueryClient()
  const { format } = useFormatCurrency()
  const isExpense = item.type === 'EXPENSE'
  const [date, setDate] = useState(item.nextDueDate)
  const [amountText, setAmountText] = useState(String(item.amount))
  const [updateExpected, setUpdateExpected] = useState(false)
  const [pickedMonth, setPickedMonth] = useState<string | null>(null)

  const history = useQuery({
    queryKey: recurringHistoryKey(item.id),
    queryFn: () => getRecurringHistory(item.id),
  })
  const paidBefore = (history.data?.entries ?? []).filter(
    (e) => e.kind === 'PAID' && e.amount != null
  )
  const lastPaid = paidBefore[0]

  // Learned from the due date, not the picked one: a salary that lands early still covers the
  // month it was due for.
  const suggestion = useQuery({
    queryKey: [
      'spend-month-suggestion',
      item.nextDueDate,
      item.categoryId,
      item.financialAccountId,
    ],
    queryFn: () =>
      fetchSpendMonthSuggestion({
        date: item.nextDueDate,
        categoryId: item.categoryId,
        financialAccountId: item.financialAccountId,
      }),
    enabled: !isExpense,
  })
  const own = monthOf(date || item.nextDueDate)
  const allowed = (m: string | null | undefined) => m === own || m === addMonths(own, 1)
  const suggested = suggestion.data?.spendMonth ?? monthOf(item.nextDueDate)
  const spendMonth = allowed(pickedMonth)
    ? pickedMonth!
    : allowed(suggested)
      ? suggested
      : suggested < own
        ? own
        : addMonths(own, 1)

  const amountPaise = parsePaise(amountText)
  const expectedPaise = toPaise(item.amount)
  const valid = amountPaise != null && amountPaise > 0 && !!date
  const diffPaise = amountPaise != null && amountPaise > 0 ? amountPaise - expectedPaise : 0
  const splits = item.splits ?? []
  const sharesPaise = valid
    ? scaleSharesPaise(
        splits.map((s) => toPaise(s.amount)),
        expectedPaise,
        amountPaise
      )
    : splits.map((s) => toPaise(s.amount))
  const yoursPaise = (valid ? amountPaise : expectedPaise) - sharesPaise.reduce((a, b) => a + b, 0)

  // Mirrors the server: an estimate becomes the average of this payment and the two before it.
  const averaged = valid
    ? [amountPaise, ...paidBefore.slice(0, 2).map((e) => toPaise(e.amount!))]
    : []
  const nextEstimatePaise = averaged.length
    ? Math.round(averaged.reduce((a, b) => a + b, 0) / averaged.length)
    : expectedPaise

  const mutation = useMutation({
    mutationFn: () =>
      markDone(item.id, {
        date,
        amount: fromPaise(amountPaise!),
        ...(!isExpense && { spendMonth }),
        ...(updateExpected && { updateExpected: true }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      onDone(
        `${item.name} marked ${isExpense ? 'paid' : 'received'} · ${format(fromPaise(amountPaise!))} on ${formatShortDate(date)}`
      )
    },
    onError: (err) => toast.error(errorDetail(err) ?? 'Failed to mark as done. Please try again.'),
  })

  const diffTone =
    diffPaise === 0
      ? ''
      : isExpense
        ? 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]'
        : diffPaise > 0
          ? 'bg-[var(--fl-income-bg)] text-[var(--fl-income)]'
          : 'bg-[var(--fl-expense-bg)] text-[var(--fl-expense)]'
  const verb = isExpense ? 'paid' : 'received'

  return (
    <Sheet title={`Mark ${item.name} as ${verb}`} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (valid && !mutation.isPending) mutation.mutate()
        }}
      >
        <p className="-mt-2 text-sm text-[var(--fl-text-2)]">
          Due {formatShortDate(item.nextDueDate)}
          {item.financialAccountName &&
            ` · ${isExpense ? 'from' : 'into'} ${item.financialAccountName}`}
          {splits.length > 0 && ` · split with ${splits.map((s) => s.contactName).join(', ')}`}
        </p>

        <div>
          <label htmlFor="mark-done-date" className={LABEL_CLS}>
            {isExpense ? 'Date paid' : 'Date received'}
          </label>
          <DatePicker id="mark-done-date" value={date} onChange={setDate} />
        </div>

        <div>
          <label htmlFor="mark-done-amount" className={LABEL_CLS}>
            Amount
          </label>
          <Input
            id="mark-done-amount"
            inputMode="decimal"
            autoFocus
            onFocus={(e) => e.target.select()}
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            aria-invalid={!valid && !!date}
            className="font-serif text-lg font-semibold tabular-nums"
          />
          {amountPaise == null || amountPaise <= 0 ? (
            <p className="mt-1 text-xs text-[var(--fl-error)]">
              Enter an amount above {format(0)}.{' '}
              {isExpense
                ? 'If it wasn’t paid this time, use Mark as Missed.'
                : 'If nothing came in this time, use Mark as Missed.'}
            </p>
          ) : (
            <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-[var(--fl-text-2)]">
              {diffPaise !== 0 ? (
                <span className={cn('rounded-md px-1.5 py-px font-semibold', diffTone)}>
                  {format(fromPaise(Math.abs(diffPaise)))} {diffPaise > 0 ? 'more' : 'less'} than
                  expected
                </span>
              ) : (
                <span>Expected {format(item.amount)}</span>
              )}
              {diffPaise !== 0 ? (
                <span>Expected {format(item.amount)}</span>
              ) : (
                lastPaid && <span>Last time {format(lastPaid.amount!)}</span>
              )}
            </div>
          )}
        </div>

        {!isExpense && (
          <SpendMonthField
            date={date}
            value={spendMonth}
            onChange={(month, byUser) => {
              if (byUser) setPickedMonth(month)
            }}
          />
        )}

        {splits.length > 0 && (
          <div className="space-y-1 rounded-lg bg-[var(--fl-surface-subtle)] px-3 py-2 text-xs text-[var(--fl-text-2)]">
            <div className="flex justify-between gap-2">
              <span>Your share</span>
              <span className="font-semibold tabular-nums text-[var(--fl-text-1)]">
                {format(fromPaise(yoursPaise))}
              </span>
            </div>
            {splits.map((s, i) => (
              <div key={s.contactId} className="flex justify-between gap-2">
                <span>{s.contactName ?? 'Someone'} owes you</span>
                <span className="font-semibold tabular-nums text-[var(--fl-text-1)]">
                  {format(fromPaise(sharesPaise[i]))}
                </span>
              </div>
            ))}
          </div>
        )}

        {item.amountVaries ? (
          <p className="text-xs text-[var(--fl-text-2)]">
            Next estimate ~{format(fromPaise(nextEstimatePaise))}
            {averaged.length > 1 && `, the average of the last ${averaged.length}`}
          </p>
        ) : (
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-[var(--fl-text-1)]">
            <Checkbox
              className="mt-0.5"
              checked={updateExpected}
              onChange={(e) => setUpdateExpected(e.target.checked)}
            />
            <span>
              Use this as the expected amount from now on
              {updateExpected && valid && diffPaise !== 0 && (
                <span className="block text-xs text-[var(--fl-text-2)]">
                  {item.name} will expect {format(fromPaise(amountPaise))} from the next one
                </span>
              )}
            </span>
          </label>
        )}

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!valid || mutation.isPending}
            className="flex-1 font-semibold"
          >
            {mutation.isPending ? 'Saving…' : isExpense ? 'Mark paid' : 'Mark received'}
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
